import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import Chip from '../../components/Chip';
import { useNavigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { useEffect, useMemo, useState } from 'react';
import { useDeleteProduct, useGetProductsList, useGetProductCategories } from "../../queries/products";
import { useGetBrands } from "../../queries/brand";
import { useGetActiveOrganiser } from "../../queries/user";
import { ActionButton } from '../../components/Forms/ActionButton';
import { useHeader } from "../../context/HeaderContext";
const EditIcon = require("../../utils/media/icons/edit_icon.png");

const ProductOrServiceList = () => {

  const navigate = useNavigate();
  const { data, isPending } = useGetProductsList();
  const { data: brandsData, isPending: brandsLoading } = useGetBrands();
  const { data: categoriesData, isPending: categoriesLoading } = useGetProductCategories();
  const { data: organisersData, isPending: organisersLoading } = useGetActiveOrganiser();
  const { mutateAsync: deleteData, isPending: isDeleting } = useDeleteProduct();
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    name: '',
    category: '',
    type: '',
    brand: '',
    client: '',
  });

  // Extract unique brands and clients for dropdown options
  const brandOptions = useMemo(() => {
    if (!brandsData) return [];
    return brandsData
      .filter(brand => brand.isActive) // Only active brands
      .map(brand => ({
        id: brand._id,
        name: brand.brandName
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [brandsData]);

  const categoryOptions = useMemo(() => {
    if (!categoriesData) return [];
    return categoriesData
      .filter(category => category.isActive !== false) // Include active categories
      .map(category => ({
        id: category._id,
        name: category.name
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [categoriesData]);

  const clientOptions = useMemo(() => {
    if (!organisersData) return [];
    return organisersData
      .filter(organiser => organiser.active) // Only active organisers
      .map(organiser => ({
        id: organiser._id,
        name: organiser.companyName || organiser.ngoName || organiser.name
      }))
      .filter(client => client.name) // Remove entries without names
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [organisersData]);

  const { setHeaderTitle } = useHeader();

  useEffect(() => {
    setHeaderTitle('Products/Services');
    return () => {
      setHeaderTitle('');
    };
  }, [setHeaderTitle]);

  useEffect(() => {
    if (data) {
      setFilteredData(data);
    }
  }, [data]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    let filtered = data;

    if (filters.status) {
      const isActive = filters.status === 'active';
      filtered = filtered.filter((product) => product.active === isActive);
    }

    if (filters.name) {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    if (filters.category) {
      filtered = filtered.filter((product) =>
        product.categoryId?._id === filters.category
      );
    }

    if (filters.type) {
      filtered = filtered.filter((product) =>
        product.type?.toLowerCase() === filters.type.toLowerCase()
      );
    }

    if (filters.brand) {
      filtered = filtered.filter((product) =>
        product.brandId?._id === filters.brand
      );
    }

    if (filters.client) {
      filtered = filtered.filter((product) => {
        const companyId = product.companyId?._id;
        const ngoId = product.ngoId?._id;
        return companyId === filters.client || ngoId === filters.client;
      });
    }

    setFilteredData(filtered);
  };

  const handleDelete = async (id) => {
    const confirm = window.confirm("Are you sure you want to delete this product/service?");
    if (!confirm) return;
    if (isDeleting) return;
    try {
      await deleteData({ id, toast }, {
        // onSuccess: () => {
        //   toast.success("Product/Service deleted successfully");
        // },
        // onError: () => {
        //   toast.error("Failed to delete product/service");
        // }
      });
    } catch (error) {
      toast.error("Failed to delete product/service");
    }
  }

  const columnHelper = createColumnHelper();

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'srno',
      header: 'Sr No.',
      cell: info => info.row.index + 1,
    }),
    columnHelper.accessor('name', {
      header: 'Name',
    }),
    columnHelper.accessor('companyId', {
      header: 'Client',
      cell: info => {
        const product = info.row.original;
        if (product.companyId?.companyName) {
          return product.companyId.companyName;
        } else if (product.ngoId?.ngoName) {
          return product.ngoId.ngoName;
        } else if (product.ngoId?.name) {
          return product.ngoId.name;
        }
        return 'N/A';
      },
    }),
    columnHelper.accessor('categoryId.name', {
      header: 'Category',
      cell: info => info.getValue() || 'N/A',
    }),
    columnHelper.accessor('type', {
      header: 'Type',
      align: 'center',
      cell: info => (
        <Chip
          value={info.getValue()}
          color={info.getValue() === 'Product' ? 'blue' : 'purple'}
          small={true}
        />
      ),
    }),
    // columnHelper.accessor('code', {
    //   header: 'Code',
    //   cell: info => {
    //     const price = info.getValue();
    //     return price ? info.getValue() : 'N/A';
    //   },
    // }),
    columnHelper.accessor('shortDescription', {
      header: 'Description',
      cell: info => (
        <div
          className="line-clamp-1 max-w-xs"
          dangerouslySetInnerHTML={{ __html: info.getValue() || 'N/A' }}
        />
      ),
    }),
    columnHelper.accessor('imageUrl', {
      header: 'Image',
      cell: info => (
        info.getValue() ? (
          <img
            src={info.getValue()}
            alt="Product/Service"
            className="size-8 object-cover rounded"
          />
        ) : (
          <span className="text-gray-400">No Image</span>
        )
      ),
    }),
    columnHelper.accessor('isActive', {
      header: 'Status',
      align: 'center',
      size: 10,
      cell: info => <Chip value={info.getValue() ? 'Active' : 'Inactive'} color={info.getValue() ? 'green' : 'inactive'} />,
      filterFn: 'equals',
      meta: {
        filterVariant: 'select',
        filterOptions: [
          { value: true, label: 'Active' },
          { value: false, label: 'Inactive' }
        ]
      }
    }),
    columnHelper.accessor('actions', {
      header: 'Actions',
      align: 'center',
      enableColumnFilter: false,
      cell: info => {
        return (
          <div className='flex items-center justify-center'>
            <button
              className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
              onClick={() => navigate('/products/products-main/create-products', { state: { _id: info.row.original?._id } })}
            >
              <img src={EditIcon} alt="edit" />
            </button>
            <button
              className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
              onClick={() => {
                handleDelete(info.row.original._id);
              }}
            >
              <FaTrash className="fill-chipRed" />
            </button>
          </div>
        )
      },
    }),
  ], [navigate]);

  if (isPending || brandsLoading || categoriesLoading || organisersLoading) {
    return (
      <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
        <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
          <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
            <h1 className="self-start text-lg font-semibold">Products/Services</h1>
            <p>Loading...</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
      {/* Filters */}
      <form
        className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] "
        onSubmit={handleSearch}
      >
        <div className='flex items-center justify-between pr-5'>
          <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
              alt=""
              className="object-contain shrink-0 w-5 aspect-square"
            />
            <span className="self-stretch">Filters</span>
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
              alt=""
              className="object-contain shrink-0 aspect-square w-[18px]"
            />
          </div>
          <ActionButton
            variant={'reset'}
            text={'Create New Product/Service'}
            handleSubmit={() => navigate('/products/products-main/create-products')}
          />
        </div>
        <div className="flex flex-wrap gap-4 p-5">
          <div className='flex gap-3 w-full'>
            <div className="flex-col w-full">
              <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Status
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          <div className='flex gap-3 w-full'>
            <div className="flex-col w-full">
              <label htmlFor="name" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Name
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="text"
                  name="name"
                  value={filters.name}
                  onChange={handleFilterChange}
                  placeholder="Enter Product/Service Name"
                  className='outline-none focus:ring-0 w-full'
                />
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="category" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Category
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                >
                  <option value="">All Categories</option>
                  {categoryOptions.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="type" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Type
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="type"
                  value={filters.type}
                  onChange={handleFilterChange}
                  className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                >
                  <option value="">All</option>
                  <option value="product">Product</option>
                  <option value="service">Service</option>
                </select>
              </div>
            </div>
          </div>
          <div className='flex gap-3 w-full'>
            <div className="flex-col w-full">
              <label htmlFor="brand" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Brand
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="brand"
                  value={filters.brand}
                  onChange={handleFilterChange}
                  className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                >
                  <option value="">All Brands</option>
                  {brandOptions.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="client" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Client (Company/NGO)
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="client"
                  value={filters.client}
                  onChange={handleFilterChange}
                  className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                >
                  <option value="">All Clients</option>
                  {organisersData.map((client) => (
                    <option key={client._id} value={client._id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex-col w-full">
              {/* Empty div to maintain layout */}
            </div>
          </div>
        </div>
        <div className='flex gap-3 items-center justify-end p-5'>
          <ActionButton
            variant={'reset'}
            text={'Reset'}
            handleSubmit={() => {
              setFilters({
                status: '',
                name: '',
                category: '',
                type: '',
                brand: '',
                client: '',
              });
              setFilteredData(data);
            }}
          />
          <ActionButton
            variant={'submit'}
            text={'Search'}
            handleSubmit={() => { }}
          />
        </div>
      </form>

      <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
        <div className="flex flex-col pr-px w-full max-md:max-w-full pl-6">
          <h1 className="self-start text-lg font-bold pb-4">Products/Services</h1>
          {filteredData && filteredData.length > 0 ? (
            <TanStackTable
              data={filteredData}
              columns={columns}
              enablePagination={true}
            />
          ) : (
            <div className="pb-4">
              No products/services available
            </div>
          )}
        </div>
      </section>
      <ToastContainer
        position="top-right"
        autoClose={3000}
      />
    </main>
  );
}

export default ProductOrServiceList;